import { isBuilt, PHASE_NAME, type Phase } from '@concord/domain';
import type { ReactNode } from 'react';
import { cn } from './cn';

/** "P3", with the phase name as its accessible description. */
export function PhaseTag({ phase, className }: { phase: Phase; className?: string }) {
  return (
    <span
      className={cn('phase-tag', className)}
      title={`Arrives in Phase ${phase.slice(1)}: ${PHASE_NAME[phase]}`}
    >
      {phase}
      <span className="sr-only">
        {' '}
        (arrives in Phase {phase.slice(1)}, {PHASE_NAME[phase]})
      </span>
    </span>
  );
}

/**
 * Wraps anything that belongs to a later phase. It renders dimmed and inert with its
 * phase marker; when the phase is built, it renders its children unchanged.
 */
export function PhaseGate({
  phase,
  children,
  className,
  tag = true,
}: {
  phase: Phase;
  children: ReactNode;
  className?: string;
  tag?: boolean;
}) {
  if (isBuilt(phase)) return <>{children}</>;
  return (
    <div className={cn('phase-block', className)} aria-disabled="true" inert>
      {children}
      {tag && <PhaseTag phase={phase} />}
    </div>
  );
}

/** A button that belongs to a later phase: disabled, marked, never clickable. */
export function PhaseButton({
  phase,
  children,
  variant = 'sec',
  className,
}: {
  phase: Phase;
  children: ReactNode;
  variant?: 'primary' | 'sec' | 'ghost';
  className?: string;
}) {
  return (
    <button
      type="button"
      className={cn('btn', variant !== 'primary' && variant, className)}
      disabled
      aria-disabled="true"
      title={`Arrives in Phase ${phase.slice(1)}: ${PHASE_NAME[phase]}`}
    >
      {children}
      <PhaseTag phase={phase} />
    </button>
  );
}
